/**
 * Click-to-verify landing for the password-reset email — mirrors
 * `bimgo-ai-web/client/src/lib/authConfirm.ts` (#204 there).
 *
 * A `{{ .ConfirmationURL }}` link is verified by GoTrue on a plain GET, so a
 * company mail scanner (e.g. Microsoft Safe Links) that opens it first uses up
 * the one-time token. The Reset Password template instead links to
 * `/auth/confirm` with the token hash in the fragment:
 *
 *   {{ .RedirectTo }}#token_hash={{ .TokenHash }}&type=recovery
 *
 * and the token is only spent by `verifyOtp` when the user clicks the button —
 * scanners load pages (and run their JS) but don't click.
 *
 * **`{{ .RedirectTo }}`, not `{{ .SiteURL }}`.** The Supabase project is shared
 * with `bimgo-ai-web`, whose host is the Site URL; a template built on it sends
 * a desktop user's link to the web client's `/auth/confirm`. `RedirectTo` is the
 * `redirectTo` this build sent (`PASSWORD_RESET_REDIRECT`), so each origin gets
 * its own link back.
 *
 * The fragment never reaches a server, and it is moved into sessionStorage at
 * startup so the rest of the page never sees the token in the address bar.
 */

export type ConfirmLink = { tokenHash: string; type: "recovery" };

export const AUTH_CONFIRM_PATH = "/auth/confirm";

const STORAGE_KEY = "auth_confirm_link";

export function parseConfirmLink(search: string, hash: string): ConfirmLink | null {
  for (const raw of [hash, search]) {
    const params = new URLSearchParams(raw.replace(/^[#?]/, ""));
    const tokenHash = params.get("token_hash");
    // Recovery only: this app sends no magic links or invites of its own.
    if (tokenHash && params.get("type") === "recovery") return { tokenHash, type: "recovery" };
  }
  return null;
}

/**
 * Runs once at startup, before the first render: on /auth/confirm, stash the
 * link for the page and strip it from the URL (no history entry).
 */
export function captureConfirmLink(): void {
  const { pathname, search, hash } = window.location;
  if (pathname !== AUTH_CONFIRM_PATH) return;

  const link = parseConfirmLink(search, hash);
  if (link) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(link));
  window.history.replaceState(window.history.state, "", AUTH_CONFIRM_PATH);
}

/** The stashed link — survives a refresh until it has been used. */
export function readConfirmLink(): ConfirmLink | null {
  try {
    const link = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? "null");
    return typeof link?.tokenHash === "string" && link?.type === "recovery"
      ? { tokenHash: link.tokenHash, type: "recovery" }
      : null;
  } catch {
    return null;
  }
}

export function clearConfirmLink(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}
